import { useQuery } from "@tanstack/react-query"
import { Mail, MapPin, Phone, ReceiptText, Scale } from "lucide-react"
import { useState } from "react"

import { api } from "@/api/cliente"
import { DatosDePagoProveedor } from "@/componentes/catalogos/DatosDePagoProveedor"
import type { Detalle } from "@/componentes/catalogos/FormularioProveedor"
import { aCentimos, comoMonto } from "@/componentes/tesoreria/dinero"
import { EstadoDeCuenta, type SaldoProveedor } from "@/componentes/tesoreria/EstadoCuentaProveedor"
import { Boton } from "@/componentes/ui/Boton"
import { Cajon } from "@/componentes/ui/Cajon"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

type OrdenResumen = {
  id: number
  codigo: string
  estado: string
  concepto: string
  monto_neto: string
  creada_en: string
}

type Perfil = {
  proveedor: Detalle
  ordenes_por_estado: Record<string, number>
  cxp_abiertas: number
  cxp_saldo: string
  gastos: string[]
  ultimas_ordenes: OrdenResumen[]
}

/**
 * La ficha del proveedor: con quién se habla, cómo se le paga y qué se le debe.
 *
 * ⚠ **Todo esto se calcula al pedirlo; no hay tabla de perfil.** Una copia de
 * «cuánto se le debe» sería una segunda fuente de verdad que se queda vieja en
 * cuanto alguien abona una cuenta por pagar.
 *
 * ⚠ **El saldo con la agencia sale del estado de cuenta, no se guarda**: lo que se le
 * debe y lo que él debe —saldo disponible de la empresa—, por moneda. Desde el estado
 * de cuenta se registra que devolvió ese saldo.
 *
 * ⚠ **Los datos de pago sólo aparecen aquí y donde se paga** (`DatosDePagoProveedor`,
 * 03/11): cuentas, pago móvil, Zelle, Binance. No viajan en el listado —`ProveedorOut` no
 * los declara— porque no hacen falta para elegir un proveedor de una lista y se
 * derramarían en cada carga de pantalla.
 */
export function PerfilProveedor({
  proveedorId,
  onCerrar,
  onEditar,
}: {
  proveedorId: number
  onCerrar: () => void
  onEditar: () => void
}) {
  const consulta = useQuery({
    queryKey: ["proveedor-perfil", proveedorId],
    queryFn: () => api.get<Perfil>(`/api/v1/proveedores/${proveedorId}/perfil`),
  })

  const usuario = usarSesion((e) => e.usuario)
  const puedeVerSaldo =
    tienePermiso(usuario, "tesoreria:ver") || tienePermiso(usuario, "orden_pago:ver_todas")
  const saldo = useQuery({
    queryKey: ["estado-cuenta-proveedor", proveedorId],
    queryFn: () => api.get<SaldoProveedor>(`/api/v1/saldos-proveedores/${proveedorId}`),
    enabled: puedeVerSaldo,
  })
  const [viendoCuenta, setViendoCuenta] = useState(false)

  const perfil = consulta.data
  const p = perfil?.proveedor

  // El estado de cuenta se abre EN LUGAR de la ficha: dos cajones apilados no se leen.
  if (viendoCuenta && p) {
    return (
      <EstadoDeCuenta
        proveedorId={proveedorId}
        nombre={p.nombre}
        onCerrar={() => setViendoCuenta(false)}
      />
    )
  }

  return (
    <Cajon abierto titulo={p ? p.nombre : "Proveedor"} onCerrar={onCerrar}>
      {consulta.isPending && <EstadoCarga etiqueta="Cargando el perfil…" />}
      {consulta.isError && (
        <EstadoError
          titulo="No se pudo cargar el perfil."
          error={consulta.error}
          onReintentar={() => consulta.refetch()}
        />
      )}
      {perfil && p && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <Insignia tono={p.activo ? "exito" : "neutro"}>
              {p.activo ? "Activo" : "Archivado"}
            </Insignia>
            <Insignia tono={p.condicion_pago === "CREDITO" ? "info" : "neutro"}>
              {p.condicion_pago === "CREDITO" ? `Crédito ${p.dias_credito} días` : "Contado"}
            </Insignia>
            {p.contribuyente_especial && <Insignia tono="aviso">Contribuyente especial</Insignia>}
            <span className="text-sm tabular-nums" style={{ color: "var(--fg-muted)" }}>
              {p.codigo} · {p.rif ?? "sin RIF"}
            </span>
          </div>

          <section className="grid gap-2 text-sm">
            {p.email && (
              <a href={`mailto:${p.email}`} className="flex items-center gap-2 underline">
                <Mail size={14} /> {p.email}
              </a>
            )}
            {/* wa.me, igual que en el resto del sistema: el humano pulsa, no hay API. */}
            {p.telefono && (
              <a
                href={`https://wa.me/${p.telefono}`}
                className="flex items-center gap-2 underline"
                target="_blank"
                rel="noreferrer"
              >
                <Phone size={14} /> {p.telefono}
              </a>
            )}
            {p.direccion && (
              <p className="flex items-start gap-2" style={{ color: "var(--fg-muted)" }}>
                <MapPin size={14} className="mt-0.5 shrink-0" /> {p.direccion}
              </p>
            )}
          </section>

          <DatosDePagoProveedor proveedorId={proveedorId} />

          <section className="grid grid-cols-2 gap-3">
            <Dato
              etiqueta="Cuentas por pagar abiertas"
              valor={String(perfil.cxp_abiertas)}
              ayuda={`Saldo ${perfil.cxp_saldo}`}
            />
            <Dato
              etiqueta="Órdenes de pago"
              valor={String(Object.values(perfil.ordenes_por_estado).reduce((a, b) => a + b, 0))}
              ayuda={
                Object.entries(perfil.ordenes_por_estado)
                  .map(([estado, n]) => `${n} ${estado.toLowerCase().replace("_", " ")}`)
                  .join(" · ") || "ninguna todavía"
              }
            />
          </section>

          {saldo.data && (
            <section className="space-y-2">
              <h3 className="text-sm font-semibold">Saldo con la agencia</h3>
              {saldo.data.saldos.length === 0 && (
                <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
                  Sin saldo: no se le debe nada ni él debe nada.
                </p>
              )}
              <ul className="space-y-1 text-sm">
                {saldo.data.saldos.map((s) => {
                  const neto = aCentimos(s.neto)
                  return (
                    <li key={s.divisa} className="flex flex-wrap justify-between gap-2">
                      <span style={{ color: "var(--fg-muted)" }}>
                        {neto > 0n
                          ? "A favor del proveedor (la agencia le debe)"
                          : neto < 0n
                            ? "Disponible de la empresa (él le debe)"
                            : "Sin saldo"}
                      </span>
                      <strong className="tabular-nums">
                        {comoMonto(neto < 0n ? -neto : neto)} {s.divisa}
                      </strong>
                    </li>
                  )
                })}
              </ul>
              <Boton tamano="sm" onClick={() => setViendoCuenta(true)}>
                <Scale size={14} /> Ver estado de cuenta
              </Boton>
            </section>
          )}

          {perfil.gastos.length > 0 && (
            <section className="space-y-1">
              <h3 className="text-sm font-semibold">Proveedor habitual de</h3>
              <ul className="flex flex-wrap gap-2">
                {perfil.gastos.map((gasto) => (
                  <li key={gasto}>
                    <Insignia tono="neutro" icono={<ReceiptText size={12} />}>
                      {gasto}
                    </Insignia>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="space-y-2">
            <h3 className="text-sm font-semibold">Últimas órdenes</h3>
            {perfil.ultimas_ordenes.length === 0 && (
              <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
                Todavía no se le ha emitido ninguna orden de pago.
              </p>
            )}
            <ul className="space-y-1 text-sm">
              {perfil.ultimas_ordenes.map((orden) => (
                <li key={orden.id} className="flex items-center justify-between gap-3">
                  <span>
                    <span className="tabular-nums">{orden.codigo}</span> · {orden.concepto}
                  </span>
                  <span className="tabular-nums">{orden.monto_neto}</span>
                </li>
              ))}
            </ul>
          </section>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={onEditar}
              className="rounded-[var(--radius-control)] px-3 py-1.5 text-sm"
              style={{ background: "var(--primary)", color: "var(--primary-fg)" }}
            >
              Editar ficha
            </button>
          </div>
        </div>
      )}
    </Cajon>
  )
}

function Dato({ etiqueta, valor, ayuda }: { etiqueta: string; valor: string; ayuda?: string }) {
  return (
    <div className="tarjeta p-3">
      <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
        {etiqueta}
      </p>
      <p className="text-xl font-semibold tabular-nums">{valor}</p>
      {ayuda && (
        <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
          {ayuda}
        </p>
      )}
    </div>
  )
}
